'use client';

import type { Voice } from 'ml-text-service-client';
import { useState, useEffect, useMemo, JSX } from 'react';
import { useDebounce } from 'react-use';
import { twMerge } from 'tailwind-merge';

import { Icon, Text, TextAreaInput } from '@videoblocks/design-system';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { useVoiceoverTabContext } from '../_context/VoiceoverTabContext';
import { ContentEditSegment } from '../types';
import GenerateButton from './GenerateButton';
import { GradientBorder } from './TextInput/GradientBorder';
import { TextInputAvailableCredits } from './TextInput/TextInputAvailableCredits';
import { TextInputCharacterCounter } from './TextInput/TextInputCounter';
import { TextInputDropdown } from './TextInput/TextInputDropdown';
import { TextInputModelSelect } from './TextInput/TextInputModelSelect';
import { TextInputPlayButton } from './TextInput/TextInputPlayButton';

const MAX_INPUT_LENGTH = 4500;

export type TextInputProps = {
    onSubmit: () => void;
    buttonText: string;
    voiceId: Voice['voice_id'] | null;
    playingVoiceId: Voice['voice_id'] | null;
    script: string;
    speed: number;
    calculatedCost: number;
    showVoicePreview?: boolean;
    showModelSelect?: boolean;
    placeholder?: string;
    submitDisabled?: boolean;
};

const TextInput = ({
    onSubmit,
    voiceId,
    playingVoiceId,
    script = '',
    speed,
    calculatedCost = 0,
    buttonText,
    showVoicePreview = false,
    showModelSelect = false,
    submitDisabled = false,
    placeholder,
}: TextInputProps): JSX.Element => {
    const { updateState, textToSpeech } = useVoiceoverTabContext();
    const [inputError, setInputError] = useState<string | undefined>(undefined);
    const {
        contentEditSegment,
        credits,
        voices,
        contributorVoices,
        CreditsApi: { calculateCost },
    } = useVoiceoverContext();

    // Find selected voice in either AI voices or contributor voices
    // AI voices take priority if the same ID exists in both arrays (unlikely but defensive)
    const selectedVoiceId = textToSpeech?.selectedVoice;
    const selectedAiVoice = voices.find((voice) => voice.voice_id === selectedVoiceId);
    const selectedContributorVoice = contributorVoices.find((voice) => voice.voice_id === selectedVoiceId);
    const selectedVoice = selectedAiVoice ?? selectedContributorVoice;
    const isContributorVoice = !selectedAiVoice && !!selectedContributorVoice;

    const displayedButtonText = useMemo(() => {
        const preCannedText = (
            {
                [ContentEditSegment.Unsubscribed]: 'Subscribe to Generate',
                [ContentEditSegment.UpgradableUser]: 'Upgrade to Generate',
                [ContentEditSegment.Disabled]: 'Disabled',
            } as Partial<Record<ContentEditSegment, string>>
        )[contentEditSegment];

        if (preCannedText) {
            return preCannedText;
        }

        if (credits === 0) return 'Purchase Credits';

        return buttonText;
    }, [contentEditSegment, credits, buttonText]);

    useDebounce(
        async () => {
            if (!script || script.length === 0 || !voiceId) return 0;

            try {
                const cost = await calculateCost(script);
                updateState({ calculatedCost: Math.abs(cost) });
            } catch (err) {
                console.error('calculateCost failed:', err);
            }
            return 0;
        },
        250,
        [script, voiceId],
    );

    useEffect(() => {
        if (script.length > MAX_INPUT_LENGTH) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInputError(
                `Your script exceeds the maximum character limit of ${MAX_INPUT_LENGTH}. Please shorten your message to continue.`,
            );
        } else {
            setInputError(undefined);
        }
    }, [script]);
    const hasInputError = !!inputError;

    return (
        <>
            <GradientBorder className={hasInputError ? 'bg-none' : ''}>
                <div className="text-left">
                    <div
                        className={twMerge(
                            // TODO: need to update our design-tokens once we have better support for dark mode
                            'flex flex-col bg-[#1d2220] rounded-xl min-h-88 max-h-160',
                            hasInputError && 'border-2 border-red-400',
                        )}
                    >
                        <div className="m-1 p-4 bg-[#13191d] shadow-[0_1px_1.5px_rgba(255,255,255,0.07)] rounded-lg relative flex-1 min-h-0 flex flex-col justify-between">
                            <div className="absolute top-4">
                                <Icon name="starFourSided" className="w-5 h-5" />
                            </div>
                            <TextAreaInput
                                textAreaClassName="w-full min-h-48 text-base border-none pl-7"
                                className="dark:bg-[#13191d] p-0"
                                keyboardFocusOnly
                                name="script-input"
                                data-testid="script-input"
                                placeholder={placeholder}
                                value={script}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                    updateState({ script: e.target.value })
                                }
                            />
                            <div className="flex justify-between items-center gap-4 p-4 pb-0">
                                <TextInputCharacterCounter current={script.length} max={MAX_INPUT_LENGTH} />
                                <TextInputAvailableCredits credits={credits} />
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row items-center justify-between md:items-end m-3 gap-2 rounded-bl-lg rounded-br-lg">
                            <div className="flex flex-col md:flex-row gap-2 items-center w-full md:w-auto">
                                {showVoicePreview && selectedVoice && (
                                    <TextInputPlayButton
                                        preview_url={selectedVoice.preview_url}
                                        voiceId={selectedVoice.voice_id}
                                        playingVoiceId={playingVoiceId}
                                        onPlayHandler={(voiceId: string) => updateState({ selectedVoice: voiceId })}
                                        voiceName={selectedVoice.name}
                                        isContributorVoice={isContributorVoice}
                                    />
                                )}
                                <TextInputDropdown
                                    value={speed}
                                    minValue={70} // min and max values are set based on ElevenLabs' API limits. but not written in the docs...
                                    maxValue={120}
                                    onChange={(speed) => updateState({ voiceoverParams: { speed } })}
                                    header="Tempo"
                                />
                                {showModelSelect && (
                                    <TextInputModelSelect
                                        value={textToSpeech.modelId}
                                        onChange={(modelId) => updateState({ modelId })}
                                    />
                                )}
                            </div>
                            <div className="w-full md:w-auto flex flex-row gap-2 items-end md:items-start">
                                <GenerateButton
                                    className="mt-4 sm:mt-0 w-full"
                                    disabled={!!inputError || submitDisabled}
                                    onSubmit={() => {
                                        if (script.length === 0) {
                                            setInputError('Please enter a script to generate voiceover audio.');
                                            return;
                                        }
                                        onSubmit();
                                    }}
                                    calculatedCost={calculatedCost}
                                >
                                    {displayedButtonText}
                                </GenerateButton>
                            </div>
                        </div>
                    </div>
                </div>
            </GradientBorder>
            {hasInputError && (
                <Text fontWeight="regular" className="text-red-100 text-left mt-2">
                    {inputError}
                </Text>
            )}
        </>
    );
};

export default TextInput;
